/**
 * ActionButtons — the platform's button hierarchy for actions we don't want to emphasize
 * (ORCH-424). Primary actions stay a plain MUI `<Button variant="contained">`.
 *
 * - SecondaryButton: Cancel, Back, Close, Stay and other neutral actions. Plain neutral text,
 *   no border at rest, light hover background.
 * - DestructiveButton: Delete, Remove, Deactivate. Plain red text, no border at rest; pass a
 *   `startIcon` where it helps. The final confirm of a destructive confirmation dialog is that
 *   dialog's primary action, so it stays `variant="contained" color="error"`.
 *
 * Variant and colour are fixed here so every screen reads the same; outlined buttons are reserved
 * for two equally valid choices. See coding_guidelines.md §"Button hierarchy".
 */

import React from 'react';
import { Button, ButtonProps } from '@mui/material';

export type ActionButtonProps = Omit<ButtonProps, 'variant' | 'color'>;

export const SecondaryButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ sx, ...props }, ref) => (
    <Button
      ref={ref}
      variant="text"
      color="inherit"
      sx={[{ color: 'text.secondary' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
    />
  )
);
SecondaryButton.displayName = 'SecondaryButton';

export const DestructiveButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  (props, ref) => <Button ref={ref} variant="text" color="error" {...props} />
);
DestructiveButton.displayName = 'DestructiveButton';
